import { Alert, Box, Button, Container, Stack, Typography } from "@mui/material";
import { Component, ReactNode } from "react";
import { getAppPublicRootUrl } from "../core";

interface AuthErrorBoundaryProps {
  children: ReactNode;
}

interface AuthErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class AuthErrorBoundary extends Component<AuthErrorBoundaryProps, AuthErrorBoundaryState> {
  constructor(props: AuthErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error) {
    console.error("Auth Error Boundary caught error:", error);

    // Check if this is a refresh token error
    const isRefreshTokenError =
      error.message.includes("Missing Refresh Token") ||
      error.message.includes("refresh token") ||
      error.message.includes("invalid_grant");

    if (isRefreshTokenError) {
      console.warn("Detected expired/invalid refresh token, clearing Auth0 cache");
      this.clearAuth0Cache();
    }
  }

  clearAuth0Cache = () => {
    // Clear Auth0 cache from localStorage
    // Auth0 stores keys with specific prefixes
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith("@@auth0spajs@@") ||
          key.startsWith("auth0.") ||
          key === "palette-mode" ||
          key === "typography-font")
      ) {
        // Don't remove UI preferences (theme + typography)
        if (key !== "palette-mode" && key !== "typography-font") {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  };

  handleRetry = () => {
    this.clearAuth0Cache();
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleRelogin = () => {
    // Clear Auth0 cache and reload to force re-authentication
    this.clearAuth0Cache();
    window.location.href = getAppPublicRootUrl();
  };

  render() {
    if (this.state.hasError) {
      const isAuthError =
        this.state.error?.message.includes("auth") ||
        this.state.error?.message.includes("token") ||
        this.state.error?.message.includes("login");

      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              py: 4,
            }}
          >
            <Stack spacing={3} width="100%">
              <Alert severity="error">
                <Typography variant="h6" gutterBottom>
                  {isAuthError ? "Authentication Error" : "Application Error"}
                </Typography>
                <Typography variant="body2">
                  {this.state.error?.message || "An unexpected error occurred. Please try again."}
                </Typography>
              </Alert>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button variant="contained" onClick={this.handleRetry}>
                  Retry
                </Button>
                {isAuthError && (
                  <Button variant="outlined" onClick={this.handleRelogin}>
                    Re-login
                  </Button>
                )}
              </Stack>

              {process.env.NODE_ENV === "development" && this.state.error && (
                <Alert severity="info">
                  <Typography variant="body2" fontFamily="monospace">
                    {this.state.error.stack}
                  </Typography>
                </Alert>
              )}
            </Stack>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}
